import { useState, useRef, useEffect } from "react";
import { useMsal } from "@azure/msal-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { callClaude } from "../utils/claudeClient";
import { sqlRead, sqlWrite, clean as cleanStr } from "../utils/sqlClient";
import { hubspotProxy } from "../utils/hubspotSync";
import { CS_SYSTEM } from "../config/systemPrompt";
import QuoteSearch from "./QuoteSearch";
import { useIsMobile } from "../hooks/useIsMobile";
import { useNavHeight } from "../hooks/useNavHeight";
import {
  ContractReviewAssistant,
  ScheduleReviewAssistant,
  CorrespondenceReviewAssistant,
} from "./estimate/assistants/ReviewAssistants";

// ── PDF extraction system prompts ─────────────────────────────────────────────
const PDF_HEADER_SYSTEM = `You are extracting data from a vendor quote PDF for the CleanSpace estimating database.
Return ONLY a valid JSON object. No markdown, no preamble, no explanation — just the raw JSON.
Keys required:
vendor_name, vendor_id, manufacturer_name (the brand/manufacturer of the equipment — may differ from vendor), contact_name, contact_email, contact_phone,
project_name, client, category, subcategory, equipment_type, description,
qty (number or null), unit_price (number or null), total_price (number or null),
freight (number or null), lead_time_weeks (number or null), quote_valid_days (number or null),
exclusions, tariff_exposure, quote_number, notes, total_line_item_count (integer — count of ALL distinct line items in the quote),
attributes (array of objects with keys: name, value, unit, source — max 10 items).
Known vendor IDs: V001=Tristate HVAC, V002=ISAT, V003=General Aire, V004=AJ Manufacturing, V005=Cannaris, V006=Princeton Cryo, V007=Pro Comp. If unknown vendor, leave vendor_id blank.
Valid categories: HVAC — Custom / Semi-Custom AHU, HVAC — Packaged DX RTU, HVAC — Fan Powered Plenum / LFM, Humidifier, HEPA Filtration System, Seismic, Pump Skid, Controls / BAS, Panels — Wall & Ceiling, Doors & Hardware, Flooring, Pass-Throughs, Lighting, Cold Room / Freezer, Dust Collector, Lab Fixtures / Utilities, LN2 / Process Gases, Fire Protection, High Speed Roll-Up Doors, Emergency Generator, Cleanroom Certification, Air Balancing, Hydronic Balancing, Wall Protection, Other.
For subcategory: provide the specific product type within the category (examples: Bollards, Handrails, Corner Guards, Bumper Rails, Wall Guards, Door Frames — leave blank if not applicable).
CRITICAL: All numeric values must be plain numbers without commas (e.g. 250000 not 250,000). Keep description under 500 characters. Never use single quotes or apostrophes in any text field — write dimensions as 2x4 not 2' x 4', write contractions without apostrophes.`;

const PDF_LINEITEMS_SYSTEM = `You are extracting line items from a vendor quote PDF for the CleanSpace estimating database.
Return ONLY a valid JSON object with a single key "line_items" containing an array. No markdown, no preamble.
Each line item object must have: line_number (int), description (string max 150 chars), qty (number or null), unit (EA/LS/LF/SF/HR), unit_price (number or null), extended_price (number or null), category (string), capacity (array).
For capacity: extract measurable performance/size specs. Examples: AHU → [{type:"Airflow",value:10000,unit:"CFM"},{type:"Cooling",value:30,unit:"Tons"}]. Leave as [] if none.
CRITICAL: All numeric values must be plain numbers without commas. Never use single quotes or apostrophes in any text field — write dimensions as 2x4 not 2' x 4'. Return complete valid JSON.`;

const TEXT_EXTRACT_SYSTEM = `You are extracting structured quote data from plain text pasted by an estimator at CleanSpace US.
Return ONLY a valid JSON object. No markdown, no preamble — just raw JSON.
Keys required:
vendor_name, vendor_id, manufacturer_name (the brand/manufacturer of the equipment — may differ from vendor), contact_name, contact_email, contact_phone,
project_name, client, category, subcategory, equipment_type, description,
qty (number or null), unit_price (number or null), total_price (number or null),
freight (number or null), lead_time_weeks (number or null), quote_valid_days (number or null),
exclusions, tariff_exposure, quote_number, notes,
line_items (array — each with: line_number, description, qty, unit, unit_price, extended_price, category, capacity[]),
attributes (array of objects with keys: name, value, unit, source — max 10 items).
Known vendor IDs: V001=Tristate HVAC, V002=ISAT, V003=General Aire, V004=AJ Manufacturing, V005=Cannaris, V006=Princeton Cryo, V007=Pro Comp. If unknown vendor, leave vendor_id blank.
Valid categories: HVAC — Custom / Semi-Custom AHU, HVAC — Packaged DX RTU, HVAC — Fan Powered Plenum / LFM, Humidifier, HEPA Filtration System, Seismic, Pump Skid, Controls / BAS, Panels — Wall & Ceiling, Doors & Hardware, Flooring, Pass-Throughs, Lighting, Cold Room / Freezer, Dust Collector, Lab Fixtures / Utilities, LN2 / Process Gases, Fire Protection, High Speed Roll-Up Doors, Emergency Generator, Cleanroom Certification, Air Balancing, Hydronic Balancing, Wall Protection, Other.
For subcategory: provide the specific product type within the category (examples: Bollards, Handrails, Corner Guards, Bumper Rails, Wall Guards, Door Frames — leave blank if not applicable).
CRITICAL: All numeric values must be plain numbers without commas. Keep description under 500 characters. Never use single quotes or apostrophes in any text field — write dimensions as 2x4 not 2' x 4', write contractions without apostrophes.`;

const IMAGE_EXTRACT_SYSTEM = `You are extracting data from a vendor quote for the CleanSpace estimating database.
Return ONLY a JSON object with these exact keys:
{
  "vendor_name": "", "vendor_id": "", "contact_name": "", "contact_email": "", "contact_phone": "",
  "project_name": "", "client": "", "category": "", "subcategory": "", "equipment_type": "", "description": "",
  "qty": null, "unit_price": null, "total_price": null, "freight": null,
  "lead_time_weeks": null, "quote_valid_days": null,
  "exclusions": "", "tariff_exposure": "", "quote_number": "", "notes": "",
  "line_items": [{"line_number":1,"description":"","qty":null,"unit":"EA","unit_price":null,"extended_price":null,"category":"","capacity":[{"type":"","value":null,"unit":""}]}],
  "attributes": [{"name":"","value":"","unit":"","source":"From Quote"}]
}
Match vendor_id: V001=Tristate HVAC, V002=ISAT, V003=General Aire Systems, V004=AJ Manufacturing, V005=Cannaris, V006=Princeton Cryo, V007=Pro Comp.
Valid categories: HVAC — Custom / Semi-Custom AHU, HVAC — Packaged DX RTU, HVAC — Fan Powered Plenum / LFM, Humidifier, HEPA Filtration System, Seismic, Pump Skid, Controls / BAS, Panels — Wall & Ceiling, Doors & Hardware, Flooring, Pass-Throughs, Lighting, Cold Room / Freezer, Dust Collector, Lab Fixtures / Utilities, LN2 / Process Gases, Fire Protection, High Speed Roll-Up Doors, Emergency Generator, Cleanroom Certification, Air Balancing, Hydronic Balancing, Wall Protection, Other.
For subcategory: provide the specific product type within the category (examples: Bollards, Handrails, Corner Guards, Bumper Rails, Wall Guards, Door Frames — leave blank if not applicable).
CRITICAL: All numeric values must be plain numbers without commas. Never use single quotes or apostrophes in any text field — write dimensions as 2x4 not 2' x 4'. No preamble, no markdown fences.`;

// ── Helpers ───────────────────────────────────────────────────────────────────
function cleanNum(v) {
  if (v === null || v === undefined || v === "") return null;
  const n = parseFloat(String(v).replace(/,/g, ""));
  return isNaN(n) ? null : n;
}

// Formats a raw numeric string for display in a money input, e.g. "$5,694,546".
// draft.total_price is stored as raw digits (no $/commas) so cleanNum still
// parses it on save; a trailing "." and decimals are preserved as typed.
function fmtMoneyInput(raw) {
  if (raw === null || raw === undefined || raw === "") return "";
  const s = String(raw).replace(/[^0-9.]/g, "");
  if (s === "") return "";
  const [intPart, ...rest] = s.split(".");
  const intFmt = intPart === "" ? "" : Number(intPart).toLocaleString("en-US");
  return "$" + intFmt + (s.includes(".") ? "." + rest.join("") : "");
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload  = () => resolve(reader.result.split(",")[1]);
    reader.onerror = () => reject(new Error("File read failed"));
    reader.readAsDataURL(file);
  });
}

// ── Blob storage ──────────────────────────────────────────────────────────────
const BLOB_ACCOUNT   = "estassist";
const BLOB_CONTAINER = "quote-docs";
const BLOB_SAS       = import.meta.env.VITE_QUOTE_DOCS_SAS || "";
const BLOB_BASE      = `https://${BLOB_ACCOUNT}.blob.core.windows.net/${BLOB_CONTAINER}`;

async function hashFile(file) {
  const buf    = await file.arrayBuffer();
  const digest = await crypto.subtle.digest("SHA-256", buf);
  return Array.from(new Uint8Array(digest)).map(b => b.toString(16).padStart(2, "0")).join("");
}

async function blobExists(blobPath) {
  try {
    const res = await fetch(`${BLOB_BASE}/${blobPath}?${BLOB_SAS}`, { method: "HEAD" });
    return res.ok;
  } catch { return false; }
}

async function uploadBlob(file, vendorName) {
  const hash      = await hashFile(file);
  const safeName  = (vendorName || "unknown").replace(/[^a-zA-Z0-9-_]/g, "_").toLowerCase();
  const blobPath  = `quotes/${safeName}/${hash}.pdf`;
  const exists    = await blobExists(blobPath);
  const blobUrl   = `${BLOB_BASE}/${blobPath}`;

  if (!exists) {
    const res = await fetch(`${blobUrl}?${BLOB_SAS}`, {
      method: "PUT",
      headers: {
        "x-ms-blob-type": "BlockBlob",
        "Content-Type": file.type || "application/pdf",
      },
      body: file,
    });
    if (!res.ok) throw new Error(`Blob upload failed: ${res.status} ${res.statusText}`);
  }

  return { blobUrl, isDuplicate: exists };
}

async function claudeExtract(systemPrompt, userContent, maxTokens = 3000) {
  const text = await callClaude(systemPrompt, userContent, maxTokens);
  const first = text.indexOf("{"); const last = text.lastIndexOf("}");
  if (first === -1 || last === -1) throw new Error("No JSON in response");
  return JSON.parse(text.slice(first, last + 1));
}

async function writeQuoteToSQL(extracted) {
  const cleanLineItems = items => (items || []).map(li => ({
    ...li,
    description:    cleanStr(li.description),
    unit:           cleanStr(li.unit),
    category:       cleanStr(li.category),
    qty:            cleanNum(li.qty),
    unit_price:     cleanNum(li.unit_price),
    extended_price: cleanNum(li.extended_price),
    capacity: (li.capacity || []).map(c => ({
      ...c,
      type:  cleanStr(c.type),
      unit:  cleanStr(c.unit),
      value: cleanNum(c.value),
    })),
  }));

  try {
    const data = await sqlWrite({
      vendor_name:       cleanStr(extracted.vendor_name     || ""),
      manufacturer_name: cleanStr(extracted.manufacturer_name || ""),
      trade:             cleanStr(extracted.trade           || extracted.category || ""),
      subcategory:       cleanStr(extracted.subcategory     || ""),
      quote_date:        extracted.quote_date      || "",
      total_amount:      cleanNum(extracted.total_amount || extracted.total_price) || 0,
      scope_area_sf:     cleanNum(extracted.scope_area_sf) || null,
      scope:             cleanStr(extracted.scope           || extracted.description || ""),
      exclusions:        cleanStr(extracted.exclusions      || ""),
      lead_time_weeks:   cleanNum(extracted.lead_time_weeks),
      valid_until:       extracted.valid_until     || null,
      source_file:       cleanStr(extracted.source_file     || ""),
      source_file_url:   extracted.source_file_url || null,
      notes:             cleanStr(extracted.notes           || ""),
      hubspot_deal_id:   extracted.hubspot_deal_id || null,
      loaded_by_name:    extracted.loaded_by_name  || null,
      loaded_by_email:   extracted.loaded_by_email || null,
      contact_name:      cleanStr(extracted.contact_name  || "") || null,
      contact_email:     cleanStr(extracted.contact_email || "") || null,
      contact_phone:     cleanStr(extracted.contact_phone || "") || null,
      line_items:        cleanLineItems(extracted.line_items),
      attributes:       (extracted.attributes || []).map(a => ({
        name:   cleanStr(a.name  || ""),
        value:  cleanStr(a.value || ""),
        unit:   cleanStr(a.unit  || ""),
        source: cleanStr(a.source || "AI_Extracted"),
      })),
    });
    return data.success !== false ? data : false;
  } catch {
    return false;
  }
}

const CATEGORIES = [
  "HVAC — Custom / Semi-Custom AHU", "HVAC — Packaged DX RTU", "HVAC — Fan Powered Plenum / LFM",
  "Humidifier", "HEPA Filtration System", "Seismic", "Pump Skid", "Controls / BAS",
  "Panels — Wall & Ceiling", "Doors & Hardware", "Flooring", "Pass-Throughs", "Lighting",
  "Cold Room / Freezer", "Dust Collector", "Lab Fixtures / Utilities", "LN2 / Process Gases",
  "Fire Protection", "High Speed Roll-Up Doors", "Emergency Generator", "Cleanroom Certification",
  "Air Balancing", "Hydronic Balancing", "Wall Protection", "Other",
];

// ── Review Modal ──────────────────────────────────────────────────────────────
function ReviewModal({ parsed, filename, onConfirm, onCancel, saving, currentUser }) {
  const [draft, setDraft] = useState({
    vendor_name:      parsed.vendor_name      || "",
    manufacturer_name: parsed.manufacturer_name || "",
    category:         parsed.category         || "",
    subcategory:      parsed.subcategory      || "",
    project_name:     parsed.project_name     || "",
    quote_number:     parsed.quote_number     || "",
    quote_date:       parsed.quote_date       || "",
    total_price:      parsed.total_price      != null ? String(parsed.total_price) : "",
    lead_time_weeks:  parsed.lead_time_weeks  != null ? String(parsed.lead_time_weeks) : "",
    scope_area_sf:    "",
    exclusions:       parsed.exclusions       || "",
    notes:            parsed.notes            || "",
    hubspot_deal_id:  "",
    contact_name:     parsed.contact_name     || "",
    contact_email:    parsed.contact_email    || "",
    contact_phone:    parsed.contact_phone    || "",
  });

  const [vendors, setVendors]               = useState([]);
  const [manufacturers, setManufacturers]   = useState([]);
  const [vendorManufacturers, setVendorManufacturers] = useState([]); // VendorManufacturers rows — db/46_vendor_manufacturers.sql, the source of truth for which manufacturers a given vendor carries
  const [catAttrs, setCatAttrs]             = useState([]);
  const [attributes, setAttributes]         = useState(parsed.attributes || []);
  const [newAttr, setNewAttr]               = useState({ name: "", value: "", unit: "" });
  const [loadingDropdowns, setLoadingDropdowns] = useState(true);
  const [dupWarning, setDupWarning]         = useState(null); // { vendor, date, quoteId } | null
  const [validationError, setValidationError] = useState(null);

  // HubSpot opportunity lookup — enter a deal ID, pull the opportunity info
  // (mirrors the Turnover / Estimate forms). The linked deal is stored on
  // draft.hubspot_deal_id; hubspotDeal holds the fetched detail for display.
  const [hubspotId, setHubspotId]           = useState(parsed.hubspot_deal_id || "");
  const [hubspotLoading, setHubspotLoading] = useState(false);
  const [hubspotError, setHubspotError]     = useState("");
  const [hubspotDeal, setHubspotDeal]       = useState(null);

  const lineItems = parsed.line_items || [];
  const set = (k, v) => setDraft(d => ({ ...d, [k]: v }));

  // Load dropdowns on mount
  useEffect(() => {
    Promise.all([
      sqlRead("EXEC sp_GetVendors"),
      sqlRead("EXEC sp_GetManufacturers"),
      sqlRead("EXEC sp_GetAllVendorManufacturers"),
    ]).then(([v, m, vm]) => {
      setVendors(v);
      setManufacturers(m);
      setVendorManufacturers(vm);
      setLoadingDropdowns(false);
    }).catch(() => setLoadingDropdowns(false));

    // Duplicate check — same vendor + quote_date
    if (parsed.vendor_name && parsed.quote_date) {
      sqlRead(`EXEC sp_DuplicateCheckQuote @vendor_name='${cleanStr(parsed.vendor_name)}', @quote_date='${cleanStr(parsed.quote_date)}'`)
        .then(rows => {
          if (rows && rows.length > 0) {
            setDupWarning({ vendor: parsed.vendor_name, date: parsed.quote_date, quoteId: rows[0].QuoteID });
          }
        })
        .catch(() => {});
    }
  }, []);

  // Load category attribute templates when category changes
  useEffect(() => {
    if (!draft.category) return;
    sqlRead(`EXEC sp_GetCategoryAttributes @category='${cleanStr(draft.category)}'`)
      .then(rows => setCatAttrs(rows))
      .catch(() => setCatAttrs([]));
  }, [draft.category]);

  const addSuggestedAttr = (attrName, attrUnit) => {
    if (attributes.find(a => a.name === attrName)) return;
    setAttributes(prev => [...prev, { name: attrName, value: "", unit: attrUnit || "", source: "Estimator" }]);
  };

  const updateAttr = (i, key, val) => {
    setAttributes(prev => prev.map((a, idx) => idx === i ? { ...a, [key]: val } : a));
  };

  const removeAttr = (i) => setAttributes(prev => prev.filter((_, idx) => idx !== i));

  const addNewAttr = () => {
    if (!newAttr.name.trim()) return;
    setAttributes(prev => [...prev, { ...newAttr, source: "Estimator" }]);
    setNewAttr({ name: "", value: "", unit: "" });
  };

  // Fetch all HubSpot owners (active + archived) once and build an
  // id -> { name, email } map. Robust against deactivated users, who 404 on
  // individual /owners/{id} lookups but still appear in the archived list.
  // (Mirrors fetchOwnerMap in Turnovers.jsx.)
  async function fetchOwnerMap() {
    const map = {};
    for (const archived of [false, true]) {
      try {
        let after;
        do {
          const url = `https://api.hubapi.com/crm/v3/owners/?limit=100&archived=${archived}${after ? `&after=${after}` : ""}`;
          const res = await hubspotProxy(url);
          (res.results || []).forEach(o => {
            map[String(o.id)] = {
              name: [o.firstName, o.lastName].filter(Boolean).join(" ") || o.email || String(o.id),
              email: o.email || "",
            };
          });
          after = res.paging?.next?.after;
          if (after) await new Promise(r => setTimeout(r, 300));
        } while (after);
      } catch {}
    }
    return map;
  }

  // Look up a HubSpot deal by ID and pull the opportunity info into the quote.
  // Stores the deal ID on draft.hubspot_deal_id and fills in the project name
  // when it's blank; the fetched detail is shown for verification.
  async function lookupHubSpot() {
    if (!hubspotId.trim()) return;
    setHubspotLoading(true);
    setHubspotError("");
    setHubspotDeal(null);
    try {
      const dealId = hubspotId.trim();
      const PROPS = "dealname,amount,closedate,hubspot_owner_id,city_state,market,dealstage,estimators";
      const deal = await hubspotProxy(
        `https://api.hubapi.com/crm/v3/objects/deals/${dealId}?properties=${PROPS}`
      );
      const p = deal.properties || {};

      const ownerMap = await fetchOwnerMap();
      const salesperson = p.hubspot_owner_id ? ownerMap[String(p.hubspot_owner_id)]?.name || "" : "";
      let estimator = "";
      if (p.estimators) {
        const estimatorId = String(p.estimators).split(/[,;]\s*/).filter(Boolean)[0];
        estimator = (estimatorId && ownerMap[estimatorId]?.name) || estimatorId || "";
      }

      // Resolve the deal-stage internal id (e.g. "appointmentscheduled") to its
      // friendly label (e.g. "Appointment Scheduled") via the deal pipelines.
      // Requires crm.schemas.deals.read (already granted). Fall back to the raw
      // id if the pipelines lookup fails.
      let dealStageLabel = p.dealstage || "";
      if (p.dealstage) {
        try {
          const pipes = await hubspotProxy("https://api.hubapi.com/crm/v3/pipelines/deals");
          for (const pipe of pipes.results || []) {
            const st = (pipe.stages || []).find(s => String(s.id) === String(p.dealstage));
            if (st) { dealStageLabel = st.label || dealStageLabel; break; }
          }
        } catch { /* keep raw id */ }
      }

      setDraft(d => ({
        ...d,
        hubspot_deal_id: dealId,
        project_name: d.project_name || p.dealname || "",
      }));
      setHubspotDeal({
        dealId,
        dealname: p.dealname || "",
        amount: p.amount || "",
        city_state: p.city_state || "",
        market: p.market || "",
        dealstage: dealStageLabel,
        salesperson,
        estimator,
      });
    } catch (e) {
      setHubspotError(e.message || "HubSpot lookup failed");
    } finally {
      setHubspotLoading(false);
    }
  }

  const handleConfirm = () => {
    const missing = [];
    if (!draft.quote_date)                        missing.push("Quote date");
    if (!draft.category)                          missing.push("Category");
    if (!cleanNum(draft.total_price))             missing.push("Total ($)");
    if (missing.length > 0) {
      setValidationError(`Please fill in required fields before saving: ${missing.join(", ")}.`);
      return;
    }
    setValidationError(null);
    onConfirm({
      ...parsed,
      vendor_name:       draft.vendor_name,
      manufacturer_name: draft.manufacturer_name,
      category:          draft.category,
      subcategory:       draft.subcategory,
      project_name:      draft.project_name,
      quote_number:      draft.quote_number,
      quote_date:        draft.quote_date       || null,
      total_price:       cleanNum(draft.total_price),
      lead_time_weeks:   cleanNum(draft.lead_time_weeks),
      scope_area_sf:     cleanNum(draft.scope_area_sf),
      exclusions:        draft.exclusions,
      notes:             draft.notes,
      hubspot_deal_id:   draft.hubspot_deal_id || null,
      loaded_by_name:    currentUser?.name     || null,
      loaded_by_email:   currentUser?.username || null,
      contact_name:      draft.contact_name    || null,
      contact_email:     draft.contact_email   || null,
      contact_phone:     draft.contact_phone   || null,
      attributes,
    });
  };

  // Manufacturer picker is scoped to the selected vendor's own known
  // manufacturers (VendorManufacturers — the source of truth for which
  // brands a vendor carries), not the site-wide list — the global list
  // (sp_GetManufacturers) is only a fallback while no known vendor is
  // selected yet. This is the ingest-time validation: an extracted name that
  // isn't already on the vendor's list is surfaced below rather than
  // silently added (sp_WriteQuote still adds it as the vendor's new
  // canonical entry on save — see db/12_vendor_contacts.sql — so nothing is
  // blocked, but the estimator sees it happening).
  const selectedVendor = vendors.find(v => v.VendorName === draft.vendor_name);
  const scopedManufacturers = selectedVendor
    ? vendorManufacturers.filter(m => m.VendorID === selectedVendor.VendorID)
    : [];
  const manufacturerOptions = selectedVendor ? scopedManufacturers : manufacturers;
  const isNewManufacturerForVendor = !!(
    selectedVendor && draft.manufacturer_name &&
    !scopedManufacturers.find(m => m.ManufacturerName.toLowerCase() === draft.manufacturer_name.toLowerCase())
  );

  return (
    <div style={modalOverlay}>
      <div style={modalBox}>
        {/* Header */}
        <div style={modalHeader}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text)" }}>Review extracted quote</div>
            {filename && <div style={{ fontSize: 12, color: "var(--text3)", marginTop: 2 }}>{filename}</div>}
          </div>
          <button onClick={onCancel} style={closeBtn}>✕</button>
        </div>

        <div style={modalBody}>

          {/* Duplicate warning */}
          {dupWarning && (
            <div style={{
              marginBottom: 14, padding: "10px 14px",
              background: "var(--amber-lt, #fffbeb)", borderRadius: "var(--radius)",
              border: "1px solid rgba(180,120,0,0.25)",
              fontSize: 13, color: "var(--text)",
              display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
            }}>
              <span>⚠️ A quote from <strong>{dupWarning.vendor}</strong> dated <strong>{dupWarning.date}</strong> already exists in the database.</span>
              <button onClick={() => setDupWarning(null)}
                style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12, color: "var(--text3)", whiteSpace: "nowrap" }}>
                Dismiss
              </button>
            </div>
          )}

          {/* Row 1: Vendor + Manufacturer */}
          <div className="mobile-stack-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
            <div>
              <div className="field-label">Vendor</div>
              {loadingDropdowns ? (
                <input type="text" value={draft.vendor_name} onChange={e => set("vendor_name", e.target.value)} />
              ) : (
                <select value={draft.vendor_name} onChange={e => set("vendor_name", e.target.value)}>
                  <option value="">— Select vendor —</option>
                  {vendors.map((v, i) => <option key={i} value={v.VendorName}>{v.VendorName}</option>)}
                  <option value={draft.vendor_name && !vendors.find(v => v.VendorName === draft.vendor_name) ? draft.vendor_name : "__other__"}>
                    {draft.vendor_name && !vendors.find(v => v.VendorName === draft.vendor_name) ? draft.vendor_name : "Other (type below)"}
                  </option>
                </select>
              )}
              {(!loadingDropdowns && (!draft.vendor_name || !vendors.find(v => v.VendorName === draft.vendor_name))) && (
                <input type="text" value={draft.vendor_name} onChange={e => set("vendor_name", e.target.value)}
                  placeholder="Type vendor name" style={{ marginTop: 6 }} />
              )}
            </div>
            <div>
              <div className="field-label">
                Manufacturer{selectedVendor && <span style={{ fontWeight: 400, color: "var(--text3)" }}> — known for {selectedVendor.VendorName}</span>}
              </div>
              {loadingDropdowns ? (
                <input type="text" value={draft.manufacturer_name} onChange={e => set("manufacturer_name", e.target.value)} />
              ) : (
                <select value={draft.manufacturer_name} onChange={e => set("manufacturer_name", e.target.value)}>
                  <option value="">— Select manufacturer —</option>
                  {manufacturerOptions.map((m, i) => <option key={i} value={m.ManufacturerName}>{m.ManufacturerName}</option>)}
                  <option value="__other__">Other (type below)</option>
                </select>
              )}
              {(!loadingDropdowns && (!draft.manufacturer_name || !manufacturerOptions.find(m => m.ManufacturerName === draft.manufacturer_name))) && (
                <input type="text" value={draft.manufacturer_name} onChange={e => set("manufacturer_name", e.target.value)}
                  placeholder="Type manufacturer name" style={{ marginTop: 6 }} />
              )}
              {isNewManufacturerForVendor && (
                <div style={{
                  marginTop: 6, padding: "6px 10px", fontSize: 11.5, color: "#92400e",
                  background: "#fffbeb", border: "1px solid rgba(180,120,0,0.25)", borderRadius: 6,
                }}>
                  ⚠️ Not yet a known manufacturer for {selectedVendor.VendorName} — saving will add it to their list.
                </div>
              )}
            </div>
          </div>

          {/* Contact info — pre-filled from AI extraction, editable before save */}
          {(draft.contact_name || draft.contact_email || draft.contact_phone) && (
            <div style={{
              marginBottom: 12, padding: "10px 12px",
              background: "var(--cs-blue-lt)", borderRadius: "var(--radius)",
              border: "1px solid rgba(24,95,165,0.15)",
            }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: "var(--cs-blue)", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                Contact extracted from quote — verify before saving
              </div>
              <div className="mobile-stack-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
                <div>
                  <div className="field-label">Contact name</div>
                  <input type="text" value={draft.contact_name} onChange={e => set("contact_name", e.target.value)} placeholder="Name" />
                </div>
                <div>
                  <div className="field-label">Email</div>
                  <input type="email" value={draft.contact_email} onChange={e => set("contact_email", e.target.value)} placeholder="email@vendor.com" />
                </div>
                <div>
                  <div className="field-label">Phone</div>
                  <input type="tel" value={draft.contact_phone} onChange={e => set("contact_phone", e.target.value)} placeholder="555-000-0000" />
                </div>
              </div>
              <div style={{ fontSize: 11, color: "var(--cs-blue)", opacity: 0.7, marginTop: 6 }}>
                New contacts are added to the vendor database automatically. Existing contacts (matched by email) are left unchanged.
              </div>
            </div>
          )}

          {/* Row 2: Category + Subcategory + Vendor Quote Number */}
          <div className="mobile-stack-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
            <div>
              <div className="field-label">Category</div>
              <select value={draft.category} onChange={e => set("category", e.target.value)}>
                <option value="">— Select category —</option>
                {CATEGORIES.map((c, i) => <option key={i} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <div className="field-label">Subcategory</div>
              <input type="text" value={draft.subcategory} onChange={e => set("subcategory", e.target.value)} placeholder="e.g. Bollards, Handrails…" />
            </div>
            <div>
              <div className="field-label">Vendor Quote Number</div>
              <input type="text" value={draft.quote_number} onChange={e => set("quote_number", e.target.value)} />
            </div>
          </div>

          {/* Row 3: Total + Lead time + Quote Date */}
          <div className="mobile-stack-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
            <div>
              <div className="field-label">Total ($)</div>
              <input
                type="text"
                inputMode="decimal"
                value={fmtMoneyInput(draft.total_price)}
                onChange={e => set("total_price", e.target.value.replace(/[^0-9.]/g, ""))}
              />
            </div>
            <div>
              <div className="field-label">Lead time (weeks)</div>
              <input type="text" value={draft.lead_time_weeks} onChange={e => set("lead_time_weeks", e.target.value)} />
            </div>
            <div>
              <div className="field-label">Quote date</div>
              <input type="date" value={draft.quote_date} onChange={e => set("quote_date", e.target.value)} />
            </div>
          </div>

          {/* Row 4: Scope Area SF */}
          <div style={{ marginBottom: 12 }}>
            <div className="field-label">Scope area (SF)</div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <input
                type="text"
                value={draft.scope_area_sf}
                onChange={e => set("scope_area_sf", e.target.value)}
                placeholder="e.g. 2400"
                style={{ maxWidth: 160 }}
              />
              <span style={{ fontSize: 12, color: "var(--text3)" }}>
                {draft.scope_area_sf && cleanNum(draft.scope_area_sf) && cleanNum(draft.total_price)
                  ? `→ $${(cleanNum(draft.total_price) / cleanNum(draft.scope_area_sf)).toFixed(2)} / SF`
                  : "SF of ceiling or floor area this quote covers — enter to enable $/SF benchmarking"}
              </span>
            </div>
          </div>

          {/* Opportunity link — enter a HubSpot deal ID and pull the deal info */}
          <div style={{ marginBottom: 12 }}>
            <div className="field-label">🔗 Link to opportunity (HubSpot Deal ID)</div>
            <div style={{ display: "flex", gap: 8 }}>
              <input
                type="text"
                value={hubspotId}
                onChange={e => setHubspotId(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); lookupHubSpot(); } }}
                placeholder="HubSpot Deal ID (e.g. 12345678)"
                style={{ flex: 1 }}
              />
              <button
                type="button"
                onClick={lookupHubSpot}
                disabled={hubspotLoading || !hubspotId.trim()}
                style={{
                  padding: "7px 16px", fontSize: 13, fontWeight: 600,
                  background: "var(--cs-blue)", color: "#fff",
                  border: "none", borderRadius: 6,
                  cursor: (hubspotLoading || !hubspotId.trim()) ? "default" : "pointer",
                  opacity: (hubspotLoading || !hubspotId.trim()) ? 0.5 : 1,
                  whiteSpace: "nowrap",
                }}
              >
                {hubspotLoading ? "Loading…" : "Import"}
              </button>
            </div>
            {hubspotError && (
              <div style={{ fontSize: 12, color: "var(--danger, #b42318)", marginTop: 6 }}>
                {hubspotError}
              </div>
            )}
            {hubspotDeal && (
              <div style={{
                marginTop: 8, padding: "10px 12px",
                background: "var(--cs-blue-lt)", borderRadius: "var(--radius)",
                border: "1px solid rgba(24,95,165,0.15)", fontSize: 12, color: "var(--text2)",
              }}>
                <div style={{ fontWeight: 600, color: "var(--text)", fontSize: 13 }}>
                  {hubspotDeal.dealname || `Deal ${hubspotDeal.dealId}`}
                </div>
                <div style={{ marginTop: 4, display: "flex", flexWrap: "wrap", gap: "2px 12px" }}>
                  {hubspotDeal.amount && <span>Value: ${Number(hubspotDeal.amount).toLocaleString()}</span>}
                  {hubspotDeal.dealstage && <span>Stage: {hubspotDeal.dealstage}</span>}
                  {hubspotDeal.city_state && <span>{hubspotDeal.city_state}</span>}
                  {hubspotDeal.market && <span>{hubspotDeal.market}</span>}
                  {hubspotDeal.salesperson && <span>Sales: {hubspotDeal.salesperson}</span>}
                  {hubspotDeal.estimator && <span>Estimator: {hubspotDeal.estimator}</span>}
                </div>
                <div style={{ marginTop: 4, color: "var(--cs-blue)", opacity: 0.8 }}>
                  Linked to HubSpot deal {hubspotDeal.dealId}
                </div>
              </div>
            )}
          </div>

          {/* Exclusions + Notes */}
          <div style={{ marginBottom: 12 }}>
            <div className="field-label">Exclusions</div>
            <textarea value={draft.exclusions} onChange={e => set("exclusions", e.target.value)} style={{ minHeight: 60 }} />
          </div>
          <div style={{ marginBottom: 16 }}>
            <div className="field-label">Notes</div>
            <textarea value={draft.notes} onChange={e => set("notes", e.target.value)} style={{ minHeight: 60 }} />
          </div>

          {/* Line items */}
          {lineItems.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div className="field-label" style={{ marginBottom: 8 }}>Line items — {lineItems.length} extracted</div>
              <div style={{ border: "1px solid var(--cs-border)", borderRadius: "var(--radius)", overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, minWidth: 480 }}>
                  <thead>
                    <tr style={{ background: "#FAFBFC", borderBottom: "1px solid var(--cs-border)" }}>
                      <th style={th}>#</th>
                      <th style={{ ...th, textAlign: "left" }}>Description</th>
                      <th style={th}>Qty</th>
                      <th style={th}>Unit</th>
                      <th style={th}>Unit $</th>
                      <th style={th}>Extended $</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lineItems.map((li, i) => (
                      <tr key={i} style={{ borderBottom: i < lineItems.length - 1 ? "1px solid var(--cs-border)" : "none" }}>
                        <td style={{ ...td, color: "var(--text3)", textAlign: "center" }}>{li.line_number}</td>
                        <td style={{ ...td, maxWidth: 220, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{li.description}</td>
                        <td style={{ ...td, textAlign: "center" }}>{li.qty ?? "—"}</td>
                        <td style={{ ...td, textAlign: "center" }}>{li.unit || "—"}</td>
                        <td style={{ ...td, textAlign: "right" }}>{li.unit_price != null ? `$${Number(li.unit_price).toLocaleString()}` : "—"}</td>
                        <td style={{ ...td, textAlign: "right" }}>{li.extended_price != null ? `$${Number(li.extended_price).toLocaleString()}` : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Attributes */}
          <div style={{ marginBottom: 8 }}>
            <div className="field-label" style={{ marginBottom: 8 }}>Attributes</div>

            {/* Category suggestions */}
            {catAttrs.length > 0 && (
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 11, color: "var(--text3)", marginBottom: 6 }}>Suggested for {draft.category}:</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {catAttrs.map((a, i) => {
                    const already = attributes.find(x => x.name === a.AttributeName);
                    return (
                      <button key={i} onClick={() => addSuggestedAttr(a.AttributeName, a.AttributeUnit)}
                        style={{
                          fontSize: 11, padding: "3px 10px", borderRadius: 4, cursor: already ? "default" : "pointer",
                          background: already ? "var(--green-lt)" : "var(--cs-blue-lt)",
                          color: already ? "var(--green)" : "var(--cs-blue)",
                          border: `1px solid ${already ? "rgba(59,109,17,0.2)" : "rgba(24,95,165,0.15)"}`,
                        }}>
                        {already ? "✓ " : "+ "}{a.AttributeName}{a.AttributeUnit ? ` (${a.AttributeUnit})` : ""}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Editable attribute rows */}
            {attributes.map((a, i) => (
              <div key={i} className="mobile-stack-attr-grid" style={{ display: "grid", gridTemplateColumns: "2fr 2fr 1fr auto", gap: 6, marginBottom: 6, alignItems: "center" }}>
                <input type="text" value={a.name} onChange={e => updateAttr(i, "name", e.target.value)} placeholder="Name" />
                <input type="text" value={a.value} onChange={e => updateAttr(i, "value", e.target.value)} placeholder="Value" />
                <input type="text" value={a.unit} onChange={e => updateAttr(i, "unit", e.target.value)} placeholder="Unit" />
                <button onClick={() => removeAttr(i)} style={{ ...closeBtn, fontSize: 13, color: "var(--red)" }}>✕</button>
              </div>
            ))}

            {/* Add new attribute */}
            <div className="mobile-stack-attr-grid" style={{ display: "grid", gridTemplateColumns: "2fr 2fr 1fr auto", gap: 6, marginTop: 8, alignItems: "center" }}>
              <input type="text" value={newAttr.name} onChange={e => setNewAttr(n => ({ ...n, name: e.target.value }))} placeholder="Attribute name" />
              <input type="text" value={newAttr.value} onChange={e => setNewAttr(n => ({ ...n, value: e.target.value }))} placeholder="Value" />
              <input type="text" value={newAttr.unit} onChange={e => setNewAttr(n => ({ ...n, unit: e.target.value }))} placeholder="Unit" />
              <button onClick={addNewAttr} style={{ ...closeBtn, fontSize: 16, color: "var(--cs-blue)", fontWeight: 700 }}>+</button>
            </div>
          </div>
        </div>

        {/* Footer */}
        {validationError && (
          <div style={{
            padding: "10px 18px", background: "var(--red-lt)",
            borderTop: "1px solid rgba(200,50,50,0.15)",
            fontSize: 13, color: "var(--red)", flexShrink: 0,
          }}>
            ⚠️ {validationError}
          </div>
        )}
        <div style={modalFooter}>
          <button onClick={onCancel} style={cancelBtn} disabled={saving}>Discard</button>
          <button onClick={handleConfirm} style={confirmBtn} disabled={saving}>
            {saving ? <><div className="spinner" />Saving…</> : "Confirm and save to database →"}
          </button>
        </div>
      </div>
    </div>
  );
}

const modalOverlay = {
  position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
  display: "flex", alignItems: "center", justifyContent: "center",
  zIndex: 1000, padding: 16,
};
const modalBox = {
  background: "var(--surface)", borderRadius: "var(--radius-lg)",
  width: "100%", maxWidth: 680, maxHeight: "90vh",
  display: "flex", flexDirection: "column",
  boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
};
const modalHeader = {
  padding: "14px 18px", borderBottom: "1px solid var(--cs-border)",
  display: "flex", justifyContent: "space-between", alignItems: "flex-start",
  flexShrink: 0,
};
const modalBody = {
  padding: "18px", overflowY: "auto", flex: 1,
};
const modalFooter = {
  padding: "12px 18px", borderTop: "1px solid var(--cs-border)",
  display: "flex", justifyContent: "flex-end", gap: 10, flexShrink: 0,
};
const closeBtn = {
  background: "none", border: "none", cursor: "pointer",
  fontSize: 16, color: "var(--text3)", padding: "2px 6px", lineHeight: 1,
};
const cancelBtn = {
  padding: "8px 16px", fontSize: 13, fontWeight: 500,
  background: "none", border: "1px solid var(--cs-border2)",
  borderRadius: "var(--radius)", cursor: "pointer", color: "var(--text2)",
};
const confirmBtn = {
  padding: "8px 20px", fontSize: 13, fontWeight: 500,
  background: "var(--cs-navy)", color: "#fff",
  border: "none", borderRadius: "var(--radius)", cursor: "pointer",
  display: "flex", alignItems: "center", gap: 8,
};
const th = { padding: "6px 10px", fontWeight: 600, color: "var(--text3)", fontSize: 11, textAlign: "center", whiteSpace: "nowrap" };
const td = { padding: "6px 10px", color: "var(--text)", fontSize: 12 };

// ── Panel: Bid Analysis ───────────────────────────────────────────────────────
function BidAnalysis({ loadingBarRef }) {
  const [model, setModel] = useState("Turnkey");
  const [level, setLevel] = useState("Competitive Bid (Level 2)");
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError]   = useState(null);

  const run = async () => {
    if (!input.trim()) return;
    setLoading(true); setResult(null); setError(null);
    if (loadingBarRef.current) loadingBarRef.current.classList.add("active");

    const extra = `Return ONLY a JSON object with keys: verdict ("BID"|"NO BID"|"CONDITIONAL BID"), verdict_rationale, market_fit ("YES"|"NO"|"REVIEW"), market_fit_note, review_tier, review_team, scope_gaps (array of strings), risk_flags (array of {risk, severity "HIGH|MEDIUM|LOW", sop_ref}), cs_gotchas (array), vendor_intelligence (array), strategy_points (array), pre_bid_questions (array). CRITICAL: All numeric values must be plain numbers without commas. No preamble, no markdown fences.`;

    try {
      const text = await callClaude(CS_SYSTEM + "\n\n" + extra, `Delivery Model: ${model}\nBid Level: ${level}\n\nBid Package:\n${input}`, 1500);
      const r = JSON.parse(text.replace(/```json|```/g, "").trim());
      setResult(r);
    } catch(e) { setError(e.message); }
    setLoading(false);
    if (loadingBarRef.current) loadingBarRef.current.classList.remove("active");
  };

  const vClass = r => r.verdict === "BID" ? "b-bid" : r.verdict === "NO BID" ? "b-nobid" : "b-cond";
  const mClass = r => r.market_fit === "YES" ? "b-bid" : r.market_fit === "NO" ? "b-nobid" : "b-cond";
  const sc = s => s === "HIGH" ? "b-high" : s === "MEDIUM" ? "b-med" : "b-low";

  return (
    <div className="panel-content">
      <div className="panel-title">Bid analysis</div>
      <div className="panel-sub">Paste in bid package details for a CleanSpace-calibrated review — market fit, risk flags, scope gaps, vendor intelligence, and strategy.</div>

      <div className="row2">
        <div>
          <div className="field-label">Delivery model</div>
          <select value={model} onChange={e => setModel(e.target.value)}>
            <option>Envelope</option>
            <option>Turnkey</option>
            <option>GC</option>
          </select>
        </div>
        <div>
          <div className="field-label">Bid level</div>
          <select value={level} onChange={e => setLevel(e.target.value)}>
            <option>Budget (Level 1)</option>
            <option>Competitive Bid (Level 2)</option>
            <option>Must Win (Level 3)</option>
          </select>
        </div>
      </div>

      <div className="field">
        <div className="field-label">Bid package details</div>
        <textarea value={input} onChange={e => setInput(e.target.value)}
          placeholder="Paste scope of work, instructions to bidders, project summary, ISO class, schedule, special requirements…" />
        <div className="tip">Include: project type, market, size, location, scope sections, bond/insurance, and anything that stood out.</div>
      </div>

      <button className="go-btn" onClick={run} disabled={loading}>
        {loading ? <><div className="spinner" />Analyzing…</> : "Analyze bid package →"}
      </button>

      {error && (
        <div className="results">
          <div className="card"><div className="card-body" style={{ color: "var(--red)" }}>
            Something went wrong: {error}
          </div></div>
        </div>
      )}

      {result && (
        <div className="results">
          <div className="card">
            <div className="card-head"><span className="card-head-icon">🎯</span><span className="card-head-title">Recommendation</span></div>
            <div className="card-body">
              <div className="verdict-row">
                <span className={`badge ${vClass(result)}`}>{result.verdict}</span>
                <span className={`badge ${mClass(result)}`}>Market fit: {result.market_fit}</span>
                <span className="badge b-tier">{result.review_tier}</span>
              </div>
              <p className="verdict-text">{result.verdict_rationale}</p>
              {result.market_fit_note && <p className="sub-note">{result.market_fit_note}</p>}
              <p className="sub-note" style={{ marginTop: 6 }}><strong>Review team:</strong> {result.review_team}</p>
            </div>
          </div>
          <div className="card">
            <div className="card-head"><span className="card-head-icon">⚠️</span><span className="card-head-title">Risk flags</span></div>
            <div className="card-body" style={{ padding: "8px 14px" }}>
              {result.risk_flags.map((rf, i) => (
                <div key={i} className="row-item">
                  <span className={`badge ${sc(rf.severity)}`}>{rf.severity}</span>
                  <div><div>{rf.risk}</div>{rf.sop_ref && <div className="sub-note">{rf.sop_ref}</div>}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="card">
            <div className="card-head"><span className="card-head-icon">🔲</span><span className="card-head-title">Scope gaps</span></div>
            <div className="card-body" style={{ padding: "8px 14px" }}>
              {result.scope_gaps.map((g, i) => <div key={i} className="row-item"><span className="dot-blue">●</span><span>{g}</span></div>)}
            </div>
          </div>
          <div className="card">
            <div className="card-head"><span className="card-head-icon">📒</span><span className="card-head-title">Vendor intelligence</span></div>
            <div className="card-body" style={{ padding: "8px 14px" }}>
              {result.vendor_intelligence.map((v, i) => <div key={i} className="row-item"><span className="dot-teal">●</span><span>{v}</span></div>)}
            </div>
          </div>
          <div className="card">
            <div className="card-head"><span className="card-head-icon">🛠️</span><span className="card-head-title">CleanSpace gotchas</span></div>
            <div className="card-body" style={{ padding: "8px 14px" }}>
              {result.cs_gotchas.map((g, i) => <div key={i} className="row-item"><span className="dot-red">●</span><span>{g}</span></div>)}
            </div>
          </div>
          <div className="card">
            <div className="card-head"><span className="card-head-icon">♟️</span><span className="card-head-title">Bid strategy</span></div>
            <div className="card-body" style={{ padding: "8px 14px" }}>
              {result.strategy_points.map((p, i) => <div key={i} className="row-item"><span className="num">{i + 1}.</span><span>{p}</span></div>)}
            </div>
          </div>
          <div className="card">
            <div className="card-head"><span className="card-head-icon">❓</span><span className="card-head-title">Pre-bid questions</span></div>
            <div className="card-body" style={{ padding: "8px 14px" }}>
              {result.pre_bid_questions.map((q, i) => <div key={i} className="row-item"><span className="dot-blue">●</span><span>{q}</span></div>)}
            </div>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 4 }}>
            <button className="reset-link" onClick={() => { setResult(null); setInput(""); }}>Analyze another bid</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Panel: Pre-Meeting Prep ───────────────────────────────────────────────────
function PreMeetingPrep({ loadingBarRef }) {
  const [project, setProject] = useState("");
  const [input, setInput]     = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult]   = useState(null);
  const [error, setError]     = useState(null);

  const run = async () => {
    if (!input.trim()) return;
    setLoading(true); setResult(null); setError(null);
    if (loadingBarRef.current) loadingBarRef.current.classList.add("active");

    const extra = `You are reviewing an estimator's pre-work before a bid strategy meeting at CleanSpace. Identify what's missing, assess readiness, and generate a structured agenda that drives strategy and actions — not discovery.
Return ONLY a JSON object:
{ readiness: "READY"|"MOSTLY READY"|"NOT READY", readiness_note: string, missing_prep: string[], agenda: [{topic, goal, time_minutes}], pre_assigned_actions: [{action, owner, priority "TODAY|THIS WEEK|NORMAL", notes}], watch_items: string[] }
CRITICAL: All numeric values must be plain numbers without commas. No preamble, no markdown fences.`;

    try {
      const text = await callClaude(CS_SYSTEM + "\n\n" + extra, `Project: ${project || "this project"}\n\nEstimator's pre-work:\n${input}`, 1500);
      const r = JSON.parse(text.replace(/```json|```/g, "").trim());
      setResult(r);
    } catch(e) { setError(e.message); }
    setLoading(false);
    if (loadingBarRef.current) loadingBarRef.current.classList.remove("active");
  };

  const rClass = r => r === "READY" ? "b-ok" : r === "NOT READY" ? "b-ready" : "b-warn";
  const pClass = p => p === "TODAY" ? "p-today" : p === "THIS WEEK" ? "p-week" : "p-normal";

  return (
    <div className="panel-content">
      <div className="panel-title">Pre-meeting prep</div>
      <div className="panel-sub">Paste in what the estimator has done so far. The assistant checks readiness, flags what's missing, and generates a structured agenda so the meeting drives strategy — not discovery.</div>
      <div className="field">
        <div className="field-label">Project name</div>
        <input type="text" value={project} onChange={e => setProject(e.target.value)} placeholder="e.g. Regeneron Phase 2 Cleanroom" />
      </div>
      <div className="field">
        <div className="field-label">Estimator's pre-work summary</div>
        <textarea value={input} onChange={e => setInput(e.target.value)}
          placeholder="Paste the estimator's notes, what they've reviewed, questions they've flagged, vendor contacts made, or a summary of where they are." />
        <div className="tip">The more detail, the sharper the readiness check and agenda.</div>
      </div>
      <button className="go-btn" onClick={run} disabled={loading}>
        {loading ? <><div className="spinner" />Building agenda…</> : "Generate meeting prep →"}
      </button>
      {error && <div className="results"><div className="card"><div className="card-body" style={{ color: "var(--red)" }}>{error}</div></div></div>}
      {result && (
        <div className="results">
          <div className="card">
            <div className="card-head"><span className="card-head-icon">📋</span><span className="card-head-title">Readiness — {project || "this project"}</span></div>
            <div className="card-body">
              <span className={`badge ${rClass(result.readiness)}`} style={{ display: "inline-block", marginBottom: 8 }}>{result.readiness}</span>
              <p className="verdict-text">{result.readiness_note}</p>
            </div>
          </div>
          {result.missing_prep.length > 0 && (
            <div className="card">
              <div className="card-head"><span className="card-head-icon">❌</span><span className="card-head-title">Missing before meeting</span></div>
              <div className="card-body" style={{ padding: "8px 14px" }}>
                {result.missing_prep.map((m, i) => <div key={i} className="row-item"><span className="dot-red">●</span><span>{m}</span></div>)}
              </div>
            </div>
          )}
          <div className="card">
            <div className="card-head"><span className="card-head-icon">📅</span><span className="card-head-title">Meeting agenda</span></div>
            <div className="card-body" style={{ padding: "8px 14px" }}>
              {result.agenda.map((a, i) => (
                <div key={i} className="row-item">
                  <span className="num">{i + 1}.</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600 }}>{a.topic}</div>
                    <div className="sub-note">{a.goal}</div>
                  </div>
                  <span style={{ fontSize: 12, color: "var(--text3)", flexShrink: 0, marginTop: 2 }}>{a.time_minutes}min</span>
                </div>
              ))}
            </div>
          </div>
          <div className="card">
            <div className="card-head"><span className="card-head-icon">✅</span><span className="card-head-title">Pre-assignable actions</span></div>
            <div className="card-body" style={{ padding: "8px 14px" }}>
              {result.pre_assigned_actions.map((a, i) => (
                <div key={i} className="row-item">
                  <span className={pClass(a.priority)}>{a.priority}</span>
                  <div style={{ flex: 1 }}>
                    <div>{a.action}</div>
                    {a.notes && <div className="sub-note">{a.notes}</div>}
                  </div>
                  <span className="b-owner">{a.owner}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="card">
            <div className="card-head"><span className="card-head-icon">👁️</span><span className="card-head-title">Things to probe in the meeting</span></div>
            <div className="card-body" style={{ padding: "8px 14px" }}>
              {result.watch_items.map((w, i) => (
                <div key={i} className="row-item">
                  <span style={{ color: "var(--purple)", fontSize: 10, flexShrink: 0, marginTop: 4 }}>●</span>
                  <span>{w}</span>
                </div>
              ))}
            </div>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 4 }}>
            <button className="reset-link" onClick={() => { setResult(null); setInput(""); setProject(""); }}>Clear</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Panel: Vendor Lookup ──────────────────────────────────────────────────────
function VendorLookup({ loadingBarRef }) {
  const [input, setInput]   = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError]   = useState(null);

  const run = async () => {
    if (!input.trim()) return;
    setLoading(true); setResult(null); setError(null);
    if (loadingBarRef.current) loadingBarRef.current.classList.add("active");

    const extra = `Answer using ONLY information from the CleanSpace vendor knowledge base. Be specific — include contact names, phone numbers, emails, prices, lead times, and exclusions where relevant. If the KB doesn't have the answer, say so clearly and suggest the next step. Plain prose, no JSON.`;

    try {
      const text = await callClaude(CS_SYSTEM + "\n\n" + extra, input, 1500);
      setResult(text);
    } catch(e) { setError(e.message); }
    setLoading(false);
    if (loadingBarRef.current) loadingBarRef.current.classList.remove("active");
  };

  return (
    <div className="panel-content">
      <div className="panel-title">Search knowledge base</div>
      <div className="panel-sub">Search CleanSpace's vendor quotes, historical pricing, lead times, contacts, and standard exclusions.</div>
      <div className="field">
        <div className="field-label">Your question</div>
        <textarea style={{ minHeight: 100 }} value={input} onChange={e => setInput(e.target.value)}
          placeholder="e.g. Who do we call for Daikin AHUs? What did ISAT charge for the Lonza seismic package? What are Tristate's standard exclusions?" />
      </div>
      <button className="go-btn" onClick={run} disabled={loading}>
        {loading ? <><div className="spinner" />Searching…</> : "Search knowledge base →"}
      </button>
      {error && <div className="results"><div className="card"><div className="card-body" style={{ color: "var(--red)" }}>{error}</div></div></div>}
      {result && (
        <div className="results">
          <div className="card">
            <div className="card-head"><span className="card-head-icon">📒</span><span className="card-head-title">Knowledge base</span></div>
            <div className="card-body"><div className="prose">{result}</div></div>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 4 }}>
            <button className="reset-link" onClick={() => { setResult(null); setInput(""); }}>Clear</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Panel: Manage KB (Quote Upload) ──────────────────────────────────────────
function ManageKB({ currentUser }) {
  // Input state
  const [pasteContent, setPasteContent] = useState("");
  const [pendingFile, setPendingFile]   = useState(null);
  const [dropStatus, setDropStatus]     = useState("idle"); // idle | ready | processing
  const fileInputRef = useRef(null);

  // Extraction state
  const [extracting, setExtracting]     = useState(false);
  const [extractError, setExtractError] = useState(null);

  // Review modal state
  const [reviewData, setReviewData]     = useState(null);  // { parsed, filename }
  const [saving, setSaving]             = useState(false);

  // Result state
  const [result, setResult]             = useState(null);

  // ── File handling ────────────────────────────────────────────────────────
  const processFile = async file => {
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (ext === "txt") {
      const text = await file.text();
      setPasteContent(text);
      setDropStatus("idle");
      return;
    }
    setPendingFile({ file, ext });
    setDropStatus("ready");
  };

  const handleDrop = e => {
    e.preventDefault();
    const files = e.dataTransfer?.files;
    if (files?.length) processFile(files[0]);
  };

  const handleFileSelect = e => {
    const files = e.target.files;
    if (files?.length) processFile(files[0]);
    e.target.value = "";
  };

  // ── Extract from file ────────────────────────────────────────────────────
  const extractFile = async () => {
    if (!pendingFile) return;
    const { file, ext } = pendingFile;
    setExtracting(true); setDropStatus("processing"); setResult(null); setExtractError(null);

    try {
      const base64 = await fileToBase64(file);
      let parsed;

      if (["jpg", "jpeg", "png"].includes(ext)) {
        parsed = await claudeExtract(IMAGE_EXTRACT_SYSTEM, [
          { type: "image", source: { type: "base64", media_type: file.type || "image/jpeg", data: base64 } },
          { type: "text", text: "Extract all quote data from this document." },
        ], 3000);
      } else if (ext === "pdf") {
        const header = await claudeExtract(PDF_HEADER_SYSTEM, [
          { type: "document", source: { type: "base64", media_type: "application/pdf", data: base64 } },
          { type: "text", text: "Extract the quote header data and count all line items." },
        ], 2000);

        const totalItems = header.total_line_item_count || 0;
        const batches = Math.max(1, Math.ceil(totalItems / 10));
        let allLineItems = [];

        for (let b = 0; b < batches; b++) {
          const startItem = b * 10 + 1;
          const endItem   = Math.min((b + 1) * 10, totalItems || 10);
          try {
            const batchResult = await claudeExtract(PDF_LINEITEMS_SYSTEM, [
              { type: "document", source: { type: "base64", media_type: "application/pdf", data: base64 } },
              { type: "text", text: totalItems > 10
                ? `Extract ONLY line items ${startItem} through ${endItem}. Return as JSON with key "line_items".`
                : `Extract ALL line items. Return as JSON with key "line_items".` },
            ], 3000);
            allLineItems = allLineItems.concat(batchResult.line_items || []);
          } catch(err) { console.warn(`Batch ${b + 1} failed:`, err.message); }
        }
        parsed = { ...header, line_items: allLineItems };
      }

      if (parsed?.vendor_name) {
        // Upload PDF to blob storage (images skipped — PDFs only)
        let uploadedBlobUrl = null;
        if (ext === "pdf") {
          try {
            const { blobUrl: url, isDuplicate } = await uploadBlob(file, parsed.vendor_name);
            uploadedBlobUrl = url;
            if (isDuplicate) console.info("Blob already exists — reusing:", url);
          } catch (uploadErr) {
            console.warn("Blob upload failed (non-fatal):", uploadErr.message);
          }
        }
        setReviewData({ parsed, filename: file.name, blobUrl: uploadedBlobUrl });
      } else {
        setExtractError("Extraction returned no vendor name — check the document and try again.");
      }
    } catch(e) {
      setExtractError(e.message);
    }

    setPendingFile(null);
    setDropStatus("idle");
    setExtracting(false);
  };

  // ── Extract from pasted text ─────────────────────────────────────────────
  const extractPaste = async () => {
    if (!pasteContent.trim()) return;
    setExtracting(true); setResult(null); setExtractError(null);

    try {
      const parsed = await claudeExtract(TEXT_EXTRACT_SYSTEM, pasteContent, 3000);
      if (parsed?.vendor_name) {
        setReviewData({ parsed, filename: null });
        setPasteContent("");
      } else {
        setExtractError("Could not identify a vendor name in the pasted text.");
      }
    } catch(e) {
      setExtractError(e.message);
    }
    setExtracting(false);
  };

  // ── Confirm save from review modal ───────────────────────────────────────
  const handleConfirmSave = async (edited) => {
    setSaving(true);
    try {
      const saved = await writeQuoteToSQL({
        ...edited,
        source_file:     reviewData.filename || "",
        source_file_url: reviewData.blobUrl  || null,
      });
      setResult({ type: "quote", parsed: edited, saved, filename: reviewData.filename, blobUrl: reviewData.blobUrl });
    } catch(e) {
      setResult({ type: "error", message: e.message });
    }
    setSaving(false);
    setReviewData(null);
  };

  return (
    <>
      {reviewData && (
        <ReviewModal
          parsed={reviewData.parsed}
          filename={reviewData.filename}
          onConfirm={handleConfirmSave}
          onCancel={() => setReviewData(null)}
          saving={saving}
          currentUser={currentUser}
        />
      )}

      <div className="panel-content">
        <div className="panel-title">Add quote</div>
        <div className="panel-sub">Upload or paste a vendor quote. Claude extracts the data and surfaces it for review before saving to the database.</div>

        {/* Current KB status */}
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-head"><span className="card-head-icon">📄</span><span className="card-head-title">Current knowledge base</span></div>
          <div className="card-body">
            <div className="prose" style={{ fontSize: 12, color: "var(--text2)" }}>
              Knowledge base is powered by Azure SQL database.<br /><br />
              <strong>Tables active:</strong> opportunities · Vendors · Contacts · Quotes · QuoteLineItems · QuoteAttributes · ReferencePricing<br />
              <strong>Data source:</strong> cs-est-sqldb on cs-avd-estdb.database.windows.net<br />
              <strong>Context injection:</strong> Logic App queries top 20 active opportunities on every request.
            </div>
          </div>
        </div>

        {/* Add new quote */}
        <div className="card">
          <div className="card-head"><span className="card-head-icon">➕</span><span className="card-head-title">Add vendor quote</span></div>
          <div className="card-body">

            {/* Paste text */}
            <div className="field-label">Paste quote text</div>
            <div className="field">
              <textarea
                value={pasteContent}
                onChange={e => setPasteContent(e.target.value)}
                style={{ minHeight: 100 }}
                placeholder="Paste a vendor quote — email body, copied PDF text, or any quote format…"
              />
            </div>
            <button className="go-btn" onClick={extractPaste} disabled={extracting || !pasteContent.trim()}>
              {extracting && !pendingFile ? <><div className="spinner" />Extracting…</> : "Extract quote from text →"}
            </button>

            {/* Divider */}
            <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "18px 0 14px" }}>
              <div style={{ flex: 1, height: 1, background: "var(--cs-border)" }} />
              <span style={{ fontSize: 12, color: "var(--text3)", whiteSpace: "nowrap" }}>or attach a file</span>
              <div style={{ flex: 1, height: 1, background: "var(--cs-border)" }} />
            </div>

            {/* Drop zone */}
            <div
              onDragOver={e => e.preventDefault()}
              onDrop={handleDrop}
              style={{
                border: "2px dashed var(--cs-border2)", borderRadius: "var(--radius-lg)",
                padding: 24, textAlign: "center", cursor: "pointer",
                marginBottom: 12, transition: "all 0.15s",
              }}
              onClick={() => fileInputRef.current?.click()}
            >
              <input ref={fileInputRef} type="file" style={{ display: "none" }}
                accept=".pdf,.jpg,.jpeg,.png,.txt,.msg"
                onChange={handleFileSelect} />

              {dropStatus === "idle" && !pendingFile && (
                <>
                  <div style={{ fontSize: 28, marginBottom: 8 }}>📎</div>
                  <div style={{ fontWeight: 500, fontSize: 14, color: "var(--text)", marginBottom: 4 }}>Drop a file here</div>
                  <div style={{ fontSize: 12, color: "var(--text3)" }}>PDF, image, or text file</div>
                </>
              )}

              {pendingFile && dropStatus !== "processing" && (
                <>
                  <div style={{ fontSize: 22, marginBottom: 6 }}>📄</div>
                  <div style={{ fontWeight: 500, fontSize: 14, color: "var(--text)", marginBottom: 2 }}>{pendingFile.file.name}</div>
                  <div style={{ fontSize: 12, color: "var(--text3)" }}>{(pendingFile.file.size / 1024).toFixed(0)} KB · {pendingFile.ext.toUpperCase()}</div>
                </>
              )}

              {dropStatus === "processing" && (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, color: "var(--text2)" }}>
                  <div className="spinner spinner-dark" />Extracting…
                </div>
              )}
            </div>

            {pendingFile && dropStatus === "ready" && (
              <button className="go-btn" style={{ marginBottom: 12 }} onClick={extractFile} disabled={extracting}>
                {extracting ? <><div className="spinner" />Extracting…</> : "Extract quote from file →"}
              </button>
            )}

            {/* Errors */}
            {extractError && (
              <div style={{ marginTop: 12, padding: "10px 14px", background: "var(--red-lt)", borderRadius: "var(--radius)", fontSize: 13, color: "var(--red)" }}>
                {extractError}
              </div>
            )}

            {/* Success result */}
            {result?.type === "quote" && (
              <div className="results">
                <div className="card">
                  <div className="card-head">
                    <span className="card-head-icon">✅</span>
                    <span className="card-head-title">Saved — {result.filename || result.parsed.vendor_name}</span>
                  </div>
                  <div className="card-body">
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
                      <span className="badge b-tier">{result.parsed.category || "Uncategorized"}</span>
                      {result.parsed.vendor_name && <span className="badge b-low">{result.parsed.vendor_name}</span>}
                      {result.parsed.total_price && <span className="badge b-tier">${Number(result.parsed.total_price).toLocaleString()}</span>}
                      {result.parsed.lead_time_weeks && <span className="badge b-warn">{result.parsed.lead_time_weeks} wks lead time</span>}
                    </div>
                    <div style={{ fontSize: 13, marginBottom: 8 }}>
                      <strong>Project:</strong> {result.parsed.project_name || "—"} &nbsp;|&nbsp;
                      <strong>Quote #:</strong> {result.parsed.quote_number || "—"} &nbsp;|&nbsp;
                      <strong>Line items:</strong> {(result.parsed.line_items || []).length} &nbsp;|&nbsp;
                      <strong>Attributes:</strong> {(result.parsed.attributes || []).length}
                    </div>
                    {result.parsed.exclusions && <div style={{ fontSize: 12, color: "var(--text2)", marginBottom: 8 }}><strong>Exclusions:</strong> {result.parsed.exclusions}</div>}
                    <p className="sub-note" style={{ marginTop: 10, color: result.saved ? "var(--green)" : "var(--amber)" }}>
                      {result.saved ? "✓ Saved to SQL database" : "⚠️ Extraction complete — SQL write failed. Check Logic App logs."}
                    </p>
                    {result.blobUrl && (
                      <p style={{ marginTop: 8, fontSize: 13 }}>
                        <a href={`${result.blobUrl}?${BLOB_SAS}`} target="_blank" rel="noopener noreferrer"
                          style={{ color: "var(--cs-blue)", textDecoration: "none", fontWeight: 500 }}>
                          📄 View original quote →
                        </a>
                      </p>
                    )}
                  </div>
                </div>
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <button className="reset-link" onClick={() => setResult(null)}>Add another quote</button>
                </div>
              </div>
            )}

            {result?.type === "error" && (
              <div className="results">
                <div className="card"><div className="card-body" style={{ color: "var(--red)" }}>Save failed: {result.message}</div></div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function EstimatingAssistant() {
  const { accounts } = useMsal();
  const navigate = useNavigate();
  // `/?panel=search` (the Quote Log's "Open Quote Search" button) opens a
  // specific panel; any other value or none falls back to the default.
  const [searchParams] = useSearchParams();
  const [activePanel, setActivePanel] = useState(() =>
    ["bid", "prep", "search", "vendor", "kb"].includes(searchParams.get("panel")) ? searchParams.get("panel") : "search"
  );
  const loadingBarRef = useRef(null);
  const isMobile = useIsMobile();
  const navHeight = useNavHeight();

  // Flat list used for the mobile tab strip — same destinations as the
  // desktop sidebar sections, minus the "All Estimates" link which gets its
  // own row since it navigates away instead of switching panels.
  const mobileTabs = [
    { id: "bid",    icon: "🔍", label: "Bid" },
    { id: "prep",   icon: "📋", label: "Prep" },
    { id: "search", icon: "🔎", label: "Search" },
    { id: "vendor", icon: "📒", label: "KB" },
    { id: "kb",     icon: "➕", label: "Add Quote" },
    { id: "asst-contract",       icon: "📃", label: "Contract" },
    { id: "asst-schedule",       icon: "📅", label: "Schedule" },
    { id: "asst-correspondence", icon: "✉️", label: "Corresp." },
  ];

  if (isMobile) {
    return (
      <>
        <div className="loading-bar" ref={loadingBarRef}><div className="loading-bar-fill" /></div>

        <div style={{ display: "flex", flexDirection: "column", height: `calc(100vh - ${navHeight}px)` }}>
          {/* All Estimates — top row, since it's a navigation away rather than a panel */}
          <button
            onClick={() => navigate("/estimates")}
            style={{
              display: "flex", alignItems: "center", gap: 8,
              padding: "10px 14px", fontSize: 13, fontWeight: 600,
              color: "var(--cs-blue)", background: "var(--cs-blue-lt)",
              border: "none", borderBottom: "1px solid var(--cs-border)",
              cursor: "pointer", width: "100%", textAlign: "left", flexShrink: 0,
            }}
          >
            <span style={{ fontSize: 15 }}>📁</span> All Estimates
          </button>

          {/* Horizontal scrollable tab strip replaces the fixed sidebar */}
          <div style={{
            display: "flex", overflowX: "auto", flexShrink: 0,
            background: "var(--surface)", borderBottom: "1px solid var(--cs-border)",
            WebkitOverflowScrolling: "touch",
          }}>
            {mobileTabs.map(item => (
              <button
                key={item.id}
                onClick={() => setActivePanel(item.id)}
                style={{
                  display: "flex", flexDirection: "column", alignItems: "center", gap: 3,
                  padding: "9px 14px", fontSize: 11, fontWeight: 600,
                  color: activePanel === item.id ? "var(--cs-blue)" : "var(--text2)",
                  background: "none", border: "none",
                  borderBottom: activePanel === item.id ? "2px solid var(--cs-blue)" : "2px solid transparent",
                  cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0,
                  minWidth: 64,
                }}
              >
                <span style={{ fontSize: 17 }}>{item.icon}</span>
                {item.label}
              </button>
            ))}
          </div>

          {/* Main content */}
          <div style={{ flex: 1, overflowY: "auto", padding: 14 }}>
            {activePanel === "bid"    && <BidAnalysis loadingBarRef={loadingBarRef} />}
            {activePanel === "prep"   && <PreMeetingPrep loadingBarRef={loadingBarRef} />}
            {activePanel === "vendor" && <VendorLookup loadingBarRef={loadingBarRef} />}
            {activePanel === "kb"     && <ManageKB currentUser={accounts[0]} />}
            {activePanel === "search" && <QuoteSearch />}
            {activePanel === "asst-contract"       && <ContractReviewAssistant loadingBarRef={loadingBarRef} />}
            {activePanel === "asst-schedule"       && <ScheduleReviewAssistant loadingBarRef={loadingBarRef} />}
            {activePanel === "asst-correspondence" && <CorrespondenceReviewAssistant loadingBarRef={loadingBarRef} />}
          </div>
        </div>

        <style>{`
          .panel-title { font-size: 16px; font-weight: 600; color: var(--text); margin-bottom: 4px; }
          .panel-sub   { font-size: 13px; color: var(--text2); margin-bottom: 20px; }
          .panel-content {}
        `}</style>
      </>
    );
  }

  return (
    <>
      <div className="loading-bar" ref={loadingBarRef}><div className="loading-bar-fill" /></div>

      <div style={{ display: "flex", height: `calc(100vh - ${navHeight}px)` }}>
        {/* Sidebar */}
        <div style={{
          width: 200, flexShrink: 0, background: "var(--surface)",
          borderRight: "1px solid var(--cs-border)", padding: "16px 0",
          display: "flex", flexDirection: "column", gap: 2,
        }}>
          <div style={{ fontSize: 10, fontWeight: 600, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.08em", padding: "0 16px", marginBottom: 6 }}>
            Knowledge base
          </div>
          <button
            onClick={() => setActivePanel("search")}
            style={{
              display: "flex", alignItems: "center", gap: 10,
              padding: "8px 16px", fontSize: 13, fontWeight: 500,
              color: activePanel === "search" ? "var(--cs-blue)" : "var(--text2)",
              background: activePanel === "search" ? "var(--cs-blue-lt)" : "none",
              border: "none", borderRight: activePanel === "search" ? "2px solid var(--cs-blue)" : "2px solid transparent",
              cursor: "pointer", width: "100%", textAlign: "left",
            }}
          >
            <span style={{ fontSize: 16 }}>🔎</span> Quote Search
          </button>
          <button
            onClick={() => setActivePanel("kb")}
            style={{
              display: "flex", alignItems: "center", gap: 10,
              padding: "8px 16px", fontSize: 13, fontWeight: 500,
              color: activePanel === "kb" ? "var(--cs-blue)" : "var(--text2)",
              background: activePanel === "kb" ? "var(--cs-blue-lt)" : "none",
              border: "none", borderRight: activePanel === "kb" ? "2px solid var(--cs-blue)" : "2px solid transparent",
              cursor: "pointer", width: "100%", textAlign: "left",
            }}
          >
            <span style={{ fontSize: 16 }}>➕</span> Add Quote
          </button>
          <button
            onClick={() => setActivePanel("vendor")}
            style={{
              display: "flex", alignItems: "center", gap: 10,
              padding: "8px 16px", fontSize: 13, fontWeight: 500,
              color: activePanel === "vendor" ? "var(--cs-blue)" : "var(--text2)",
              background: activePanel === "vendor" ? "var(--cs-blue-lt)" : "none",
              border: "none", borderRight: activePanel === "vendor" ? "2px solid var(--cs-blue)" : "2px solid transparent",
              cursor: "pointer", width: "100%", textAlign: "left",
            }}
          >
            <span style={{ fontSize: 16 }}>📒</span> Search KB
          </button>

          {/* Assistants — standalone one-off AI review tools (ports of the
              turnover contract/schedule/correspondence reviews). */}
          <div style={{ fontSize: 10, fontWeight: 600, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.08em", padding: "0 16px", margin: "16px 0 6px" }}>
            Assistants
          </div>
          {[
            { id: "asst-contract",       icon: "📃", label: "Contract Review" },
            { id: "asst-schedule",       icon: "📅", label: "Schedule Review" },
            { id: "asst-correspondence", icon: "✉️", label: "Correspondence" },
          ].map(item => (
            <button
              key={item.id}
              onClick={() => setActivePanel(item.id)}
              style={{
                display: "flex", alignItems: "center", gap: 10,
                padding: "8px 16px", fontSize: 13, fontWeight: 500,
                color: activePanel === item.id ? "var(--cs-blue)" : "var(--text2)",
                background: activePanel === item.id ? "var(--cs-blue-lt)" : "none",
                border: "none", borderRight: activePanel === item.id ? "2px solid var(--cs-blue)" : "2px solid transparent",
                cursor: "pointer", width: "100%", textAlign: "left",
              }}
            >
              <span style={{ fontSize: 16 }}>{item.icon}</span> {item.label}
            </button>
          ))}
        </div>

        {/* Main content */}
        <div style={{ flex: 1, overflowY: "auto", padding: 24 }}>
          <div style={{ maxWidth: activePanel === "search" ? "none" : 680 }}>
            {activePanel === "bid"    && <BidAnalysis loadingBarRef={loadingBarRef} />}
            {activePanel === "prep"   && <PreMeetingPrep loadingBarRef={loadingBarRef} />}
            {activePanel === "vendor" && <VendorLookup loadingBarRef={loadingBarRef} />}
            {activePanel === "kb"     && <ManageKB currentUser={accounts[0]} />}
            {activePanel === "search" && <QuoteSearch />}
            {activePanel === "asst-contract"       && <ContractReviewAssistant loadingBarRef={loadingBarRef} />}
            {activePanel === "asst-schedule"       && <ScheduleReviewAssistant loadingBarRef={loadingBarRef} />}
            {activePanel === "asst-correspondence" && <CorrespondenceReviewAssistant loadingBarRef={loadingBarRef} />}
          </div>
        </div>
      </div>

      <style>{`
        .panel-title { font-size: 16px; font-weight: 600; color: var(--text); margin-bottom: 4px; }
        .panel-sub   { font-size: 13px; color: var(--text2); margin-bottom: 20px; }
        .panel-content {}
      `}</style>
    </>
  );
}
